import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Flex, List, Button, Input, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import CustomAvatar from "../../../Components/CustomAvatar";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";
import { handleApiError } from "../../../utils/CommonFunctions";

const ArchivedUsersModal = ({ getChatListApi, userSelfData, onModalUpdate }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [totalSearchUsers, setTotalSearchUsers] = useState(null);
  const [searchCounts, setSearchCounts] = useState({});
  const [userName, setUserName] = useState([]);
  const [hoverState, setHoverState] = useState("");
  const [showLoader, setShowLoader] = useState(false);

  const fetchArchivedSuggestions = useCallback(async () => {
    try {
      const response = await ApiService.get(API_ENDPOINTS.archivedUserSuggestions);
      if (response?.data) {
        setSuggestedUsers(response.data.suggestions || []);
        setTotalSearchUsers(response.data.total_users);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, []);

  const searchArchivedUserApi = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) return;

    setShowLoader(true);
    try {
      const response = await ApiService.post(API_ENDPOINTS.searchArchivedUserList, {
        searchTerm,
      });

      if (response?.data && response?.status === 1) {
        setSearchCounts(response.data);
        setUserName(response.data.users || []);
      } else {
        setUserName([]);
        setSearchCounts({});
      }
    } catch (error) {
      handleApiError(error);
      setUserName([]);
      setSearchCounts({});
    } finally {
      setShowLoader(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(searchArchivedUserApi, 400),
    [searchArchivedUserApi]
  );

  useEffect(() => {
    fetchArchivedSuggestions();
    inputRef.current?.focus();
  }, [fetchArchivedSuggestions]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);

    if (value) {
      debouncedSearch(value);
    } else {
      setUserName([]);
      setSearchCounts({});
      fetchArchivedSuggestions();
    }
  }, [debouncedSearch, fetchArchivedSuggestions]);

  const closeModal = useCallback(() => {
    setSearchText("");
    onModalUpdate({ activeModal: "" });
  }, [onModalUpdate]);

  const addUserInChatApi = useCallback(async (id) => {
    if (!id || !userSelfData?._id) return;

    try {
      await ApiService.post(API_ENDPOINTS.addUserInChatList, {
        userId: userSelfData._id,
        selectedUserId: id,
      });
    } catch (error) {
      // ignore — user may already be in chat list
    }

    localStorage.setItem("current_id", id);
    navigate(`/elsnertech/message/${id}`);
    setUserName([]);
    closeModal();
    getChatListApi();
  }, [userSelfData?._id, navigate, getChatListApi, closeModal]);

  const filteredUsers = useMemo(() => {
    if (!searchText) return suggestedUsers;

    const lowerSearchText = searchText.toLowerCase();
    return userName.filter(
      (user) =>
        user.username?.toLowerCase().includes(lowerSearchText) ||
        user.fullName?.toLowerCase().includes(lowerSearchText)
    );
  }, [searchText, userName, suggestedUsers]);

  const memberCountText = useMemo(() => {
    if (searchText) {
      return `${searchCounts.totalSearchResults || 0} of ${searchCounts.total_users || 0} archived members`;
    }
    return `${suggestedUsers.length || 0} of ${totalSearchUsers || 0} archived members`;
  }, [searchText, searchCounts, suggestedUsers.length, totalSearchUsers]);

  const handleUserClick = useCallback(async (userId) => {
    await addUserInChatApi(userId);
  }, [addUserInChatApi]);

  const renderUserItem = useCallback((item, isSearchResult = false) => {
    const userId = isSearchResult ? item._id : item.userId;

    return (
      <List.Item
        key={userId}
        onMouseEnter={() => setHoverState(userId)}
        className={hoverState === userId ? "hoverClass" : ""}
        onClick={() => handleUserClick(userId)}
        style={{ cursor: "pointer" }}
      >
        <List.Item.Meta
          avatar={
            <CustomAvatar
              img={item?.thumbnail_avatarUrl}
              size={40}
              userName={item?.fullName || item?.username}
            />
          }
          title={
            <>
              <span className="title">{item?.username || ""}</span>
              {item?.fullName && <span className="subtitle"> - {item.fullName}</span>}
              {item?.position && <span className="designation"> - {item.position}</span>}
            </>
          }
          description={item.elsner_email || item.email}
        />
        <Button type="text" className="add-btn">
          {item.content}
        </Button>
      </List.Item>
    );
  }, [hoverState, handleUserClick]);

  return (
    <Modal
      title="Archived Users"
      open={true}
      onCancel={closeModal}
      footer={null}
      className="ec-modal direct-message-search-modal"
      width={600}
      style={{ top: 20 }}
      destroyOnHidden
    >
      <Flex gap="small" className="ant-modal-subheader">
        <div className="search-container-wrapper">
          <div className="search-container">
            <div className="search-tags-wrapper">
              <Input
                className="search-input"
                placeholder="Search archived users"
                value={searchText}
                onChange={handleInputChange}
                ref={inputRef}
              />
            </div>
          </div>
        </div>
      </Flex>

      <Flex justify="space-between" gap="small" className="member-details">
        <p>{memberCountText}</p>
      </Flex>

      <Flex vertical className="suggestion-list-container">
        {showLoader ? (
          <Spin size="default" />
        ) : (
          <>
            {searchText ? (
              filteredUsers.length > 0 ? (
                <List
                  className="suggestion-list"
                  itemLayout="horizontal"
                  dataSource={filteredUsers}
                  renderItem={(item) => renderUserItem(item, true)}
                />
              ) : (
                <p className="no-results-message">
                  No archived users found matching "{searchText}"
                </p>
              )
            ) : (
              suggestedUsers.length > 0 ? (
                <List
                  className="suggestion-list"
                  itemLayout="horizontal"
                  dataSource={suggestedUsers}
                  renderItem={(item) => renderUserItem(item, false)}
                />
              ) : (
                <p className="no-results-message">No archived users found</p>
              )
            )}
          </>
        )}
      </Flex>
    </Modal>
  );
};

export default ArchivedUsersModal;
